import React, { useMemo } from 'react';
import DatePicker, {
  CalendarContainer,
  registerLocale,
} from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import de from 'date-fns/locale/de';
import it from 'date-fns/locale/it';
import es from 'date-fns/locale/es';
import nl from 'date-fns/locale/nl';
import be from 'date-fns/locale/be';
import pl from 'date-fns/locale/pl';
import { format, parse } from 'date-fns';
import classNames from 'classnames/bind';
import { useAppSelector } from 'app/hooks';
import { selectCurrentMarket } from 'features/core/selectors';
import { getMarketDateFormat } from 'common/helpers/market';
import styles from './DateSelector.module.scss';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * @Use: https://reactdatepicker.com/
 */

interface Props {
  date: string | null;
  startDate?: Date;
  endDate?: Date;
  placeholder?: string;
  onChange: (date: string) => void;
  className?: string;
  calendarStartDay?: any;
  locale?: any;
  includeDates?: Date[];
  onBlur?:(e:any)=>void;
}

type ButtonProps = React.HTMLProps<HTMLButtonElement>;

interface ContainerProps {
  children: React.ReactNode;
}

registerLocale('fr', fr);
registerLocale('de', de);
registerLocale('es', es);
registerLocale('pl', pl);
registerLocale('it', it);
registerLocale('nl', nl);
registerLocale('be', be);

const DateSelector = ({
  date,
  startDate,
  endDate,
  placeholder,
  className,
  calendarStartDay,
  locale,
  includeDates,
  onChange,
  onBlur
}: Props) => {
  // Date format related to market
  const currentMarket = useAppSelector(selectCurrentMarket);
  const dateFormat = useMemo(
    () => getMarketDateFormat(currentMarket),
    [currentMarket],
  );
  // Custom container
  const CustomContainer = ({ children }: ContainerProps) => (
    <div className={styles.customContainer}>
      <CalendarContainer>{children}</CalendarContainer>
    </div>
  );
  // Custom button
  const CustomButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (props, ref) => (
      <button
        type="button"
        ref={ref}
        onClick={props.onClick}
        className={styles.customButton}
      >
        {props.value || placeholder || ''}
      </button>
    ),
  );

  return (
    <div className={classNames(styles.DateSelector, className)}>
      <DatePicker
        selected={date ? parse(date, dateFormat, new Date()) : null}
        startDate={startDate}
        endDate={endDate}
        onChange={(date: Date) => {
          onChange(format(date, dateFormat));
        }}
        disabledKeyboardNavigation
        onBlur={onBlur}
        calendarContainer={CustomContainer}
        customInput={<CustomButton />}
        popperClassName={styles.customPopper}
        popperPlacement="top"
        locale={locale}
        dateFormat={dateFormat}
        calendarStartDay={calendarStartDay}
        includeDates={includeDates}
      />
    </div>
  );
};

export default DateSelector;
